import React, { useRef, useState } from "react";
import { css } from "@emotion/react";
import {
  ArrowDropDown,
  AutoAwesomeOutlined,
  ExitToApp,
  FeedbackTwoTone,
  VisibilityOutlined,
} from "@mui/icons-material";
import { flexRow } from "../../styles/mixins";
import { colorVars, getColor } from "../../utils/colorUtils";
import { useRecoilState, useRecoilValue } from "recoil";
import { isAdminSelector, userAtom } from "../../atoms/user.atom";
import { useRouter } from "next/router";
import { AccessTokenStore, REFRESH_TOKEN_KEY } from "@monorepo/components";
import { useClickOutside } from "../hooks/useClickOutside";
import { adminModeAtom } from "../../atoms/adminMode.atom";
import Link from "next/link";
import { walkthroughAtom } from "../../atoms/walkthrough.atom";
import { Inbox } from "@novu/react";

const s = {
  container: css({
    display: "flex",
    justifyContent: "space-between",
    columnGap: "6.4rem",
    padding: "2.4rem 1rem 2.4rem 2.4rem",
    zIndex: 25,
    marginRight: "-3.2rem",
    backgroundColor: "white",
    position: "relative",
    marginBottom: "3.2rem",
    borderRadius: 8,
    borderTopRightRadius: 0,
    borderTopLeftRadius: 0,
    alignItems: "center",
  }),
  row: css(flexRow, {
    width: "fit-content",
  }),
  profile: css(flexRow, {
    columnGap: "1.4rem",
    cursor: "pointer",
    padding: "1.2rem",
    position: "relative",
    width: "fit-content",
    borderTopRightRadius: 8,
    borderTopLeftRadius: 8,
  }),
  icons: css(flexRow, {
    backgroundColor: "transparent",
    columnGap: 4,
    border: "none",
    width: "6rem",
    cursor: "pointer",
  }),
  notification: css({
    outline: "none",
    backgroundColor: "transparent",
    border: "none",
    padding: 0,
  }),
  feedbackButton: css({
    display: "flex",
    columnGap: "2.4rem",
    alignItems: "center",
    width: "fit-content",
    borderRadius: ".8rem",
    cursor: "pointer",
    border: "none",
    outline: "none",
    padding: "1.6rem 1.2rem",
    color: "white",
    backgroundColor: colorVars.green,

    ":focus": {
      backgroundColor: getColor("green", 0.6),
      color: "white",
      opacity: 0.8,
      p: {
        color: "white",
      },
    },
  }),
  userDropdown: css({
    position: "absolute",
    borderBottomLeftRadius: 8,
    borderBottomRightRadius: 8,
    backgroundColor: "white",
    display: "flex",
    flexDirection: "column",
    rowGap: "2.4rem",
    padding: "2.4rem 1.2rem",
    right: 0,
    top: "5.1rem",
    width: "100%",
  }),
  dropdownBtn: css({
    border: "none",
    backgroundColor: "transparent",
    fontFamily: "OpenSans, sans-serif",
    fontSize: "1.6rem",
    alignItems: "center",
    columnGap: ".8rem",
    cursor: "pointer",
    display: "flex",
  }),
};

const HeaderNav = () => {
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const user = useRecoilValue(userAtom);
  const isAdmin = useRecoilValue(isAdminSelector);
  const [adminMode, setAdminMode] = useRecoilState(adminModeAtom);
  const router = useRouter();
  const walkthroughState = useRecoilValue(walkthroughAtom);
  useClickOutside(ref, () => setDropdownOpen(false));

  function logout() {
    AccessTokenStore.set(undefined);
    localStorage.removeItem(REFRESH_TOKEN_KEY);
    router.push("/login");
  }
  return (
    <div css={s.container}>
      {isAdmin && adminMode.viewInstitution && (
        <button
          css={[s.feedbackButton, { backgroundColor: colorVars.error }]}
          onClick={() => {
            setAdminMode({ viewInstitution: null });
          }}
        >
          <VisibilityOutlined css={{ fontSize: 24, color: "white" }} />
          <p
            css={{
              color: "white",
              marginLeft: "-1.2rem",
              fontSize: "1.6rem",
              fontWeight: 600,
            }}
          >
            Previewing school: {adminMode.viewInstitution.name}
          </p>
          <p css={{ color: "white" }}>Click to stop preview</p>
        </button>
      )}
      {isAdmin && !adminMode.viewInstitution && (
        <Link
          href={"/view-school"}
          css={{
            textDecoration: "none",
          }}
        >
          <button css={s.feedbackButton}>
            <AutoAwesomeOutlined css={{ fontSize: 24, color: "white" }} />
            <p
              css={{
                color: "white",
                marginLeft: "-1.2rem",
                fontSize: "1.6rem",
                fontWeight: 600,
              }}
            >
              Preview school
            </p>
          </button>
        </Link>
      )}
      {!isAdmin && (
        <a
          href={"https://forms.gle/MmMoaGHzo47Kmn7P7"}
          css={{ textDecoration: "none" }}
          target={"_blank"}
        >
          <button css={s.feedbackButton}>
            <FeedbackTwoTone css={{ fontSize: 24, color: "white" }} />
            <p
              css={{
                color: "white",
                marginLeft: "-1.2rem",
                fontSize: "1.6rem",
                fontWeight: 600,
              }}
            >
              Share your Feedback!
            </p>
            <p css={{ color: "white", fontSize: "1.4rem" }}>
              Tell us what works, what doesn{"'"}t, and what else you{"'"}d love
              to see
            </p>
          </button>
        </a>
      )}
      {/*<button css={s.notification}>*/}
      {/*  <NotificationsNoneOutlined*/}
      {/*    css={{ fontSize: 24, color: colorVars.bluePrimary }}*/}
      {/*  />*/}
      {/*</button>*/}
      <div css={s.row}>
        <div>
          {user && process.env.NEXT_PUBLIC_NOVU_APPLICATION_IDENTIFIER && (
            <Inbox
              appearance={{
                elements: {
                  bellContainer: {
                    width: "2rem",
                    height: "2rem",
                  },
                  inbox__popoverContent: {
                    zIndex: 50,
                  },
                  tooltipContent: {
                    fontSize: "1.4rem",
                    padding: "0.6rem",
                    width: "fit-content",
                  },
                  notificationSubject: {
                    fontWeight: 700,
                    // marginBottom: "1.4rem",
                    // backgroundColor: "yellow",
                  },
                  notificationPrimaryAction__button: {
                    fontSize: "1.4rem",
                    height: "3rem",
                  },
                  inboxStatus__title: {
                    fontSize: "1.6rem",
                  },
                  preferencesHeader__title: {
                    fontSize: "1.6rem",
                  },
                },
                variables: {
                  colorPrimary: colorVars.bluePrimary,
                },
              }}
              subscriberId={user.id.toString()}
              applicationIdentifier={
                process.env.NEXT_PUBLIC_NOVU_APPLICATION_IDENTIFIER
              }
              routerPush={(path: string) => router.push(path)}
            />
          )}
        </div>
        <div
          css={[s.profile, dropdownOpen && { backgroundColor: "white" }]}
          onClick={() => setDropdownOpen(!dropdownOpen)}
          ref={ref}
        >
          <p
            css={{ whiteSpace: "nowrap" }}
            ref={walkthroughState["profile-nav"]}
          >
            {user?.name}
          </p>
          <button css={s.icons}>
            <img src={"/profile.svg"} alt="your profile icon" />
            <ArrowDropDown
              css={{ fontSize: "2rem", color: colorVars.bluePrimary }}
            />
          </button>
          {dropdownOpen && (
            <div css={s.userDropdown} onClick={(e) => e.stopPropagation()}>
              <button
                css={s.dropdownBtn}
                onClick={() => router.push("/profile")}
              >
                Your profile
              </button>
              <button css={s.dropdownBtn} onClick={() => router.push("/hub")}>
                Your DEI hub
              </button>
              <button
                css={[s.dropdownBtn, { color: colorVars.error }]}
                onClick={logout}
              >
                Logout <ExitToApp css={{ fontSize: 16 }} />
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default HeaderNav;
